import { Button, toast } from '@raystack/apsara';
import axios from 'axios';
import * as _ from 'lodash';

import store from './redux/store';

import { setIAMDialogState } from './redux/_reducers/authReducer';
import { customFetch } from './utils/CustomFetch';
import {
  CORRELATION_ID_HEADER,
  CURRENT_ORG_HEADER,
  ENTITLEMENT_MSGS
} from './utils/constants/APP';
import { generateRequestId } from './utils/requestId';
import { LogoutUser } from './utils/userHandler';

export async function addOrgIdRequestInterceptor(config) {
  const activeOrgId = customFetch.getOrg();
  config.headers[CURRENT_ORG_HEADER] = activeOrgId;
  config.headers[CORRELATION_ID_HEADER] = generateRequestId();
  return config;
}

export async function handleResponseStatusInterceptor(error) {
  if (error?.response?.status === 401) {
    await LogoutUser();
    return;
  }

  if (error?.response?.status === 403) {
    const message =
      error.response.data?.error?.message ||
      error.response.data?.detail ||
      error.response.data?.error;

    toast.error('Unauthorized!', {
      description: message
    });
    return Promise.reject(error);
  }

  if (error?.response?.status === 402) {
    const data = error?.response?.data;

    /**
     * @type {boolean} - if errorStructCheck returns false, then default fallback toast will show
     */
    const errorStructCheck = data && _.has(data, 'error');

    /**
     * @type {boolean}
     */
    const entitlement_failed =
      errorStructCheck &&
      data.error.code === ENTITLEMENT_MSGS.entitlementFailed;

    /**
     * @type {boolean}
     */
    const insufficent_credit =
      errorStructCheck &&
      data.error.code === ENTITLEMENT_MSGS.insufficientCredit;

    if (entitlement_failed) {
      toast.error('Feature unvailable!', {
        id: ENTITLEMENT_MSGS.entitlementFailed,
        description:
          'This feature is not available in your current plan. Please upgrade plan!',
        action: (
          <Button
            size='small'
            variant='outline'
            color='neutral'
            style={{ textWrap: 'nowrap' }}
            data-test-id='btn-error-toast'
            onClick={() =>
              store.dispatch(
                setIAMDialogState({ route: '/plans', isOpen: true })
              )
            }
          >
            Upgrade Plan
          </Button>
        )
      });
      return Promise.reject(error);
    }

    if (insufficent_credit) {
      toast.error('Insufficient Credits!', {
        id: ENTITLEMENT_MSGS.insufficientCredit,
        description:
          "You don't have enough credits to use this feature. Please recharge tokens!"
      });
      return Promise.reject(error);
    }

    // when 402 does not return any of the above codes or error response does not
    // have the error key in its body
    toast.error('Payment Error!', {
      id: 'payment-error',
      description:
        'We encountered some error related to payment. Kindly contact support!',
      action: (
        <Button
          size='small'
          variant='outline'
          color='neutral'
          style={{ textWrap: 'nowrap' }}
          onClick={() =>
            window.open(import.meta.env.VITE_CUSTMERSUPPORT_URL, '_blank')
          }
          data-test-id='btn-error-toast'
        >
          Support
        </Button>
      )
    });

    return Promise.reject(error);
  }
  return Promise.reject(error);
}

export const createPollInstance = baseURL => {
  const newInstance = axios.create({
    baseURL: baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  newInstance.interceptors.request.use(addOrgIdRequestInterceptor, error => {
    return Promise.reject(error);
  });
  newInstance.interceptors.response.use(
    response => {
      return response;
    },
    async function (error) {
      if (error?.response?.status === 401) {
        await LogoutUser();
        return;
      }
      return Promise.reject(error);
    }
  );

  return newInstance;
};

export const createAxiosInstance = baseURL => {
  const newInstance = axios.create({
    baseURL: baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  newInstance.interceptors.request.use(addOrgIdRequestInterceptor, error => {
    return Promise.reject(error);
  });

  newInstance.interceptors.response.use(response => {
    // console.log("token already authenticated!");
    return response;
  }, handleResponseStatusInterceptor);

  return newInstance;
};

// For atlas related APIs
const dataStore = createAxiosInstance(import.meta.env.VITE_ATLAS);

const AtlasPoll = createPollInstance(import.meta.env.VITE_ATLAS);

const ArcPoll = createPollInstance(import.meta.env.VITE_VECTOR);

const DexterPoll = createPollInstance(import.meta.env.VITE_DEXTER);

// Vector aka Arc
const Arc = createAxiosInstance(import.meta.env.VITE_VECTOR);
const dexter = createAxiosInstance(import.meta.env.VITE_DEXTER);

// For image search
const imageSearch = createAxiosInstance(import.meta.env.VITE_IMG_SEARCH);
const Cosmos = createAxiosInstance(import.meta.env.VITE_COSMOS);
const Frontier = createAxiosInstance(import.meta.env.VITE_FRONTIER_URL);

// TiTiler
const CogTiTiler = createAxiosInstance(import.meta.env.VITE_COG_TILER);
const StacTiTiler = createAxiosInstance(import.meta.env.VITE_STAC_TILER);

export default {
  imageSearch,
  dataStore,
  dexter,
  Arc,
  Cosmos,
  Frontier,
  AtlasPoll,
  ArcPoll,
  DexterPoll,
  CogTiTiler,
  StacTiTiler
};
